import { WbMoreOptions, WbTooltip } from '@agilelab/plugin-wb-platform';
import { ListItemIcon, MenuItem } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { FC, useMemo, useState } from 'react';
import { TeamRoleAssignment, TeamRoleAssignmentKindEnum } from '../../../types';
import { useTeamRolesPanelContext } from '../../../context';
import { TeamRoleEnum } from '@agilelab/plugin-wb-team-roles-common';

type TeamRoleAssignmentsActionsColumnProps = {
  assignment: TeamRoleAssignment;
  onDelete: (assignment: TeamRoleAssignment) => void;
};

export const TeamRoleAssignmentsActionsColumn: FC<
  TeamRoleAssignmentsActionsColumnProps
> = ({ assignment, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const {
    assignments,
    selectedTeamRole,
    isOwner,
    hasTroubleshootAccess,
    isLimitedOwner,
  } = useTeamRolesPanelContext();

  const isLastFull = useMemo(() => {
    if (!assignment.kinds.includes(TeamRoleAssignmentKindEnum.FULL)) {
      return false;
    }

    const principalCount = (assignments || []).reduce(
      (count, ass) =>
        count + Number(ass.kinds.includes(TeamRoleAssignmentKindEnum.FULL)),
      0,
    );

    return principalCount <= 1;
  }, [assignments, assignment]);

  const cannotDelete = useMemo(() => {
    // Early return for simple cases
    if (isLastFull && assignment.kinds.length === 1) {
      return {
        condition: true,
        message:
          'Cannot delete last full assignee, create another full assignee first',
      };
    }

    // Extract kinds once for performance
    const hasFull = assignment.kinds.includes(TeamRoleAssignmentKindEnum.FULL);

    const isOwnerRole = selectedTeamRole === TeamRoleEnum.OWNER;

    // Logic for Owner with troubleshoot access
    if ((isOwner || hasTroubleshootAccess) && isOwnerRole && hasFull) {
      return { condition: false, message: '' };
    }

    // Logic for Limited Owner
    if (isLimitedOwner && isOwnerRole) {
      return {
        condition: true,
        message: 'Limited owners cannot delete owner assignees',
      };
    }

    return { condition: false };
  }, [
    isLastFull,
    assignment.kinds,
    isOwner,
    hasTroubleshootAccess,
    isLimitedOwner,
    selectedTeamRole,
  ]);

  const onMenuClose = () => setMenuOpen(false);

  return (
    <WbMoreOptions
      open={menuOpen}
      MenuProps={{ onClick: e => e.stopPropagation() }}
      onClick={e => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
      onClose={onMenuClose}
    >
      <WbTooltip title={cannotDelete?.message || ''}>
        <span>
          <MenuItem
            disabled={cannotDelete.condition}
            onClick={() => {
              onMenuClose();
              onDelete(assignment);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        </span>
      </WbTooltip>
    </WbMoreOptions>
  );
};
